/**
 * Материализация расписания приёмов — в каркасе приложения, а не на вкладке.
 *
 * Раньше этим занимался эффект внутри «Таблетницы». Вкладка не основная —
 * «Здоровье» открывается на «Показателях», — так что построение приёмов
 * требовало двух прицельных действий: зайти в раздел и переключить сегмент.
 *
 * Горизонт материализации — 30 дней. Строки `doseEvents` читают три независимых
 * потребителя, и ни один из них их не создаёт: сетка календаря, отметка «Принял»
 * в этой сетке и планировщик напоминаний в каркасе. Поэтому у того, кто пьёт
 * лекарство долго и в таблетницу не заглядывает, через месяц приёмы кончались
 * разом везде: в календаре пусто, отмечать нечего, напоминания молчат.
 *
 * Ровно от этого уводили сами напоминания — но перенесли планировщик, а не
 * источник данных для него.
 */

import { useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { DOSE_HORIZON_DAYS, scheduleFingerprint } from '@/entities/medication/model/dose-scheduler';
import { isBoundToMeals } from '@/entities/medication/model/meal-schedule';
import { useMedications } from '@/entities/medication/model/use-medications';
import { db } from '@/shared/db/database';
import { alive } from '@/shared/db/repository';
import { getCurrentUserId } from '@/shared/db/session-context';
import { addDays, type IsoDate } from '@/shared/lib/datetime';
import { useToday } from '@/shared/lib/use-today';

import { syncAllSchedules } from './sync-doses';

/**
 * Отпечаток режима питания на горизонте: правила плюс поправки по дням.
 *
 * Нужен курсам, привязанным к еде: своего времени они не хранят, оно считается
 * из режима каждого дня. Значит, сдвиг обеда — что разовый, что в самом
 * правиле — меняет расписание приёмов, хотя сам курс остаётся прежним.
 *
 * Подписка заводится, только когда привязанный курс есть: иначе правка режима
 * дня перезапускала бы материализацию всех курсов на ровном месте.
 *
 * В ответе повторяется и сам признак: подписка отвечает не мгновенно, и до её
 * ответа виден отпечаток от прошлого значения. Без этой отметки первый заход с
 * привязанным курсом строил приёмы дважды — сперва по пустому отпечатку, потом
 * по настоящему.
 */
interface MealFingerprint {
  enabled: boolean;
  key: string;
}

function useMealFingerprint(enabled: boolean, from: IsoDate): MealFingerprint | undefined {
  const to = addDays(from, DOSE_HORIZON_DAYS);

  return useLiveQuery(async () => {
    if (!enabled) return { enabled, key: '' };
    const userId = getCurrentUserId();
    if (!userId) return { enabled, key: '' };

    const [routines, overrides] = await Promise.all([
      db.routines.where('[userId+kind]').equals([userId, 'meal']).toArray(),
      db.routineOverrides
        .where('[userId+date]')
        .between([userId, from], [userId, to], true, true)
        .toArray(),
    ]);

    // Только поля, влияющие на время еды: правка названия перезапускать
    // построение приёмов не должна.
    const rules = alive(routines)
      .map((r) => `${r.id}|${r.enabled ? 1 : 0}|${r.startMin}|${r.durationMin}|${r.weekdaysMask}`)
      .sort();

    const days = alive(overrides)
      .map((o) => `${o.routineId}@${o.date}|${o.startMin ?? ''}|${o.durationMin ?? ''}|${o.skipped ? 1 : 0}`)
      .sort();

    return { enabled, key: [...rules, ...days].join('/') };
  }, [enabled, from, to]);
}

export function useDoseMaterialization(): void {
  const medications = useMedications();

  /*
    Ключ собран из полей, которые влияют на расписание, а не из ревизии.
    С ревизией любая запись в препарат — а её делает и списание остатка при
    отметке «Принял» — перезапускала бы материализацию всех курсов: тридцать
    дней вперёд по каждому и разбор просрочек за сорок пять дней назад. Одно
    нажатие на таблетку стоило бы десятков транзакций.

    Дата в ключе тоже нужна: горизонт отсчитывается от сегодняшнего дня, и с
    наступлением новых суток его надо продлить, даже если сами курсы не
    менялись. Поэтому дата — состояние: прочитанная при отрисовке, она не
    менялась, пока приложение утром просыпалось из фона.
  */
  const day = useToday();

  // Пока отпечаток отвечает про другой набор курсов, ключ не готов: иначе
  // первый заход строил бы приёмы дважды — по пустому отпечатку и по настоящему.
  const boundToMeals = medications?.some(isBoundToMeals) ?? false;
  const meals = useMealFingerprint(boundToMeals, day);
  const pending = medications === undefined || meals === undefined || meals.enabled !== boundToMeals;

  const syncKey = pending
    ? null
    : `${day}|${meals.key}|${medications.map(scheduleFingerprint).join('|')}`;

  useEffect(() => {
    if (!medications || syncKey === null) return;
    void syncAllSchedules(medications, day);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncKey]);
}
